import { defineStore } from 'pinia'
import api from '@/api/tree'
import request from '@/utils/request'
import { deepCopy } from '@/utils'
import { useTreeStore } from '@/stores/tree'

import { THIS_MONTH } from '@/components/DatePicker'

const DEFAULT_FILTERS = {
  node: undefined,
  childNode: undefined,
  dateRange: {
    shortcut: THIS_MONTH
  },
  country: [],
}

export const useHeaderStore = defineStore('header', {
  persist: {
    pick: ['filters'],
  },
  state: () => ({
    filters: deepCopy(DEFAULT_FILTERS),
    frequentlyUsedNodes: [],
    countryData: [],
    hierarchyData: [],
    hierarchyTopData: [],
    hierarchyNodeData: [],
    displayed: {} as Record<string, any>,
  }),

  getters: {
    countryDataGetter: (state) => state.countryData,
    hierarchyDataGetter: (state) => state.hierarchyData,
    hierarchyTopDataGetter: (state) => state.hierarchyTopData,
    hierarchyNodeDataGetter: (state) => state.hierarchyNodeData,
    filterDataGetter: (state) => state.filters,
    selectedOrganisationGetter (state): any {
      const treeStore = useTreeStore()
      const selected = state.filters.node
      if (!selected) return undefined
      const match = Object
        .values(treeStore.organisations)
        .filter(row => row.root_id === selected.id)
      if (match) return match[0]
      return undefined
    },
    selectedIndustryGetter (): any {
      if (!this.filters.node) return undefined
      const org = this.selectedOrganisationGetter
      const treeStore = useTreeStore()
      const industries = treeStore.industries
      if (!org) return undefined
      return industries[org.industry]
    },
    frequentlyUsedNodesGetter: (state) => state.frequentlyUsedNodes,
  },

  actions: {
    getCountryData () {
      request.json('/api/location/countries/', {cache: true})
        .then(({ data }) => { this.countryData = data })
    },

    getHierarchyData () {
      return api.getHierarchyList()
        .then(data => {
          this.hierarchyData = data
          return data
        })
    },

    getHierarchyTopData () {
      return api.getHierarchyTopList()
        .then(({ data }) => {
          const options = [
            ...data.map(({id, label}) => ({text: label, value: id}))
          ]
          this.hierarchyTopData = options
          return options
        })
    },

    getHierarchyNodeData (nodeId) {
      return api.getHierarchyNodeList(nodeId)
        .then(({ children }) => { this.hierarchyNodeData = children })
    },

    // Mutations exposed as actions
    setHeaderDisplayed (data) { this.displayed = data },

    setCountryFilter (country) {
      this.filters = {...this.filters, country}
    },
    setDateRange (dateRange) {
      this.filters = {...this.filters, dateRange}
    },
    setNode (node) {
      this.filters = {...this.filters, node}
    },
    setChildNode (childNode) {
      this.filters = {...this.filters, childNode}
    },
    resetFilters () {
      this.filters = deepCopy(DEFAULT_FILTERS)
    },
    updateFrequentlyUsed (node) {
      if (!node) return

      const id = node && node.id.toString().replace(/^freq-/, '')
      // update the frequently used nodes
      const freq = [...this.frequentlyUsedNodes]
      const exists = freq.find(row => row.id === `freq-${id}`)
      if (exists) exists.used += 1
      else freq.push({id: `freq-${id}`, label: node.label, used: 1})
      this.frequentlyUsedNodes = freq.sort((a, b) => b.used - a.used).slice(0, 5)
    },
  }
})
